/**
 * createDongLabelCell
 *
 * Builds a standalone text-label cell for an apartment building (동) number,
 * e.g. `101동`, positioned at a given editor point. Mirrors the manual text
 * label path in `useObjectCreation` (transparent Rectangle body + visible
 * `label.text`, `data.type='point'`) so it renders, exports as point geometry,
 * and persists via `graph.toJSON()` exactly like a hand-placed label — no
 * special save/export handling needed.
 *
 * Used by the unified CCTV & 구역 매핑 flow: 동 labels extracted from the same
 * drawing PDF are placed via the same PDF→editor similarity as zones.
 */

import { dia, shapes } from '@joint/core';

import type { Point } from '../../../map-editor-shared/api/types/layerMapping';
import { DONG_LAYER_NAME } from '../../../map-editor-shared/api/dongApi';
import {
  measureLabelBox,
  LABEL_FONT_FAMILY,
  LABEL_TEXT_COLOR,
} from './labelTextMetrics';

/** `data.properties.kind` marker identifying an auto-imported 동 text label. */
export const DONG_KIND = 'dong';

/** Fallback font size when the caller cannot derive one from the graph scale. */
const DEFAULT_DONG_FONT_SIZE = 12;

/**
 * Create a 동 text-label element. Returns null for an empty name.
 *
 * `fontSize` should come from `computeLabelFontSize(graph)` so the label scales
 * with the map (parking-cell short side), matching CCTV id/name overlays.
 */
export function createDongLabelCell(args: {
  name: string;
  point: Point;
  objectTypeId: number;
  entityHandle: string;
  fontSize: number;
  layerName?: string;
  /** Text color (#hex). Defaults to LABEL_TEXT_COLOR (red, both-theme visible). */
  color?: string;
}): dia.Element | null {
  const name = args.name.trim();
  if (!name) return null;
  const fontSize = args.fontSize > 0 ? args.fontSize : DEFAULT_DONG_FONT_SIZE;
  const color = (args.color && args.color.trim()) || LABEL_TEXT_COLOR;
  const layer = args.layerName ?? DONG_LAYER_NAME;
  const { width, height } = measureLabelBox(name, fontSize);

  const label = new shapes.standard.Rectangle();
  // 중심이 args.point 에 오도록 좌상단 = 중심 - 박스 절반. export 의
  // convertElementToMapObject 가 (position + size/2) 를 point 로 읽는다.
  label.position(args.point.x - width / 2, args.point.y - height / 2);
  label.resize(width, height);
  label.attr({
    body: {
      // 'none' 은 hit-test 에서 빠지므로 transparent 로 채워 라벨 전체를 선택 가능.
      fill: 'transparent',
      stroke: 'none',
      strokeWidth: 0,
    },
    label: {
      text: name,
      fill: color,
      fontSize,
      fontFamily: LABEL_FONT_FAMILY,
      textAnchor: 'middle',
      textVerticalAnchor: 'middle',
    },
  });
  label.set('data', {
    typeId: args.objectTypeId,
    type: 'point',
    kind: 'label',
    // objectType 은 일부러 넣지 않는다(zone 셀과 동일 패턴). elementConverter 가
    // export layer 를 `data.objectType || data.layer` 순으로 고르므로 '동'(한글
    // 표시명)을 넣으면 export 최상위 키가 'dongs' 가 아니라 '동' 으로 새어나간다.
    // typeId 로 ObjectType('동') 과 연결되고, layer 는 항상 'dongs' 로 export 된다.
    layer,
    entity_handle: args.entityHandle,
    properties: {
      kind: DONG_KIND,
      name,
    },
  });
  label.prop('objectTypeId', args.objectTypeId);
  return label;
}

/** 그래프에 이미 있는 동 라벨 이름 목록 — 재실행 멱등 dedup 용. */
export function listExistingDongNames(graph: dia.Graph): string[] {
  const names: string[] = [];
  for (const cell of graph.getCells()) {
    const data = cell.get('data') as
      | { properties?: { kind?: string; name?: string } }
      | undefined;
    if (data?.properties?.kind === DONG_KIND && data.properties.name) {
      names.push(String(data.properties.name));
    }
  }
  return names;
}
